/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSatisfinderAuth } from '@/contexts/SatisfinderAuthContext';
import { API } from '@/App';
import { SATISFINDER_AGREEMENT } from '@/lib/satisfinderAgreement';
import './SatisfinderDashboard.css';

const NAV = [
  { key: 'home',         label: 'Home' },
  { key: 'profile',      label: 'Profile' },
  { key: 'invitations',  label: 'Invitations' },
  { key: 'finds',        label: 'My FINDs' },
  { key: 'ratings',      label: 'Ratings' },
  { key: 'earnings',     label: 'Earnings' },
  { key: 'achievements', label: 'Achievements' },
  { key: 'referrals',    label: 'Referrals' },
  { key: 'affiliate',    label: 'Affiliate' },
];

// Predefined, searchable language list for the Profile → Languages picker.
// Saved languages outside this list are preserved as-is.
const LANGUAGE_OPTIONS = [
  'English', 'Filipino / Tagalog', 'Mandarin Chinese', 'Hindi', 'Spanish', 'French', 'Standard Arabic',
  'Bengali', 'Portuguese', 'Russian', 'Indonesian', 'German', 'Japanese',
  'Swahili', 'Marathi', 'Telugu', 'Turkish', 'Tamil', 'Yue Chinese (Cantonese)',
  'Vietnamese', 'Korean', 'Italian', 'Thai', 'Gujarati', 'Persian (Farsi)',
  'Polish', 'Ukrainian', 'Malay', 'Cebuano', 'Ilocano',
  'Hiligaynon', 'Dutch', 'Kannada', 'Malayalam', 'Punjabi', 'Oriya (Odia)',
  'Burmese', 'Hausa', 'Yoruba', 'Igbo', 'Amharic', 'Greek', 'Czech', 'Swedish',
  'Hungarian', 'Hebrew', 'Romanian', 'Nepali', 'Sinhala', 'Khmer', 'Lao',
  'Pashto', 'Somali', 'Zulu', 'Afrikaans', 'Finnish', 'Danish', 'Norwegian',
  'Bulgarian', 'Croatian', 'Serbian', 'Slovak', 'Catalan',
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function money(v, cur) {
  if (v == null) return '—';
  const sym = { INR: '₹', PHP: '₱', EUR: '€', GBP: '£', USD: '$', AED: 'AED ', SGD: 'S$', AUD: 'A$', CAD: 'C$', JPY: '¥' }[cur] || '';
  return `${sym}${Number(v).toLocaleString()}`;
}

function initials(name) {
  return (name || 'S').split(/\s+/).map(x => x[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

export default function SatisfinderDashboard() {
  // `SatisfinderAuthContext` exposes the authenticated session under
  // `me` (session) and its bootstrap flag under `loading`. We keep
  // separate local state for the richer `/me/full` profile aggregate.
  const { me: session, loading: sessionLoading, logout } = useSatisfinderAuth();
  const authenticated = !!session;
  const navigate = useNavigate();
  const [tab, setTab] = useState('home');
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authenticated) return;
    (async () => {
      try {
        const r = await fetch(`${API}/satisfinder/me/full`, { credentials: 'include' });
        if (r.ok) setMe(await r.json());
      } finally { setLoading(false); }
    })();
  }, [authenticated]);

  if (sessionLoading) return <div className="sfp-shell"><div className="sfp-app"><div className="sfp-empty">Loading…</div></div></div>;
  if (!authenticated) return <Navigate to="/satisfinder/login" replace />;
  if (loading || !me) return <div className="sfp-shell"><div className="sfp-app"><div className="sfp-empty">Loading…</div></div></div>;

  const displayName = me.identity.first_name || me.identity.full_name?.split(' ')?.[0] || 'satisFINDER';
  const ready = me.ready_for_finds?.ready;
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="sfp-shell" data-testid="sfp-shell">
      <div className="sfp-app">
        <aside className="sfp-side" data-testid="sfp-sidebar">
          <div className="account">
            <div className="avatar" data-testid="sfp-avatar">
              {me.identity.profile_photo_url
                ? <img src={me.identity.profile_photo_url} alt="" />
                : initials(me.identity.full_name)}
            </div>
            <div className="who">
              <b data-testid="sfp-name">{me.identity.full_name || 'satisFINDER'}</b>
              <small data-testid="sfp-public-id">{me.identity.sf_public_id}</small>
            </div>
          </div>
          <div className="progress"><i style={{ width: `${me.profile_completeness}%` }} /></div>
          <div className="progress-lbl" data-testid="sfp-completeness">Profile {me.profile_completeness}% complete</div>
          {me.profile_completeness_detail?.complete ? (
            <div className="progress-lbl" data-testid="sfp-complete-note" style={{ color: 'var(--sf-blue)', fontWeight: 700 }}>Profile complete</div>
          ) : me.profile_completeness_detail?.next_action ? (
            <button
              type="button"
              className="sfp-next-action"
              onClick={() => setTab(me.profile_completeness_detail.next_action.tab)}
              data-testid="sfp-next-action"
            >{me.profile_completeness_detail.next_action.label} →</button>
          ) : null}
          <nav className="sfp-nav">
            {NAV.map(n => (
              <button
                key={n.key}
                type="button"
                className={tab === n.key ? 'active' : ''}
                onClick={() => setTab(n.key)}
                data-testid={`sfp-nav-${n.key}`}
              >{n.label}</button>
            ))}
          </nav>
          <button className="sfp-ask" onClick={() => toast('The satisFIND Assistant is coming soon.')} data-testid="sfp-ask-satisfind">
            Ask satisFIND
          </button>
          <button className="logout" onClick={() => { logout(); navigate('/satisfinder/login'); }} data-testid="sfp-logout">
            Sign out
          </button>
        </aside>

        <main className="sfp-main">
          <div className="sfp-topbar" data-testid="sfp-topbar">
            <div className="date">{dateStr}</div>
            <div className="actions">
              <button className="pill" onClick={() => setTab('profile')} data-testid="sfp-top-profile">Profile</button>
            </div>
          </div>

          {tab === 'home' && <HomeView me={me} greetingName={displayName} ready={ready} goTo={setTab} />}
          {tab === 'invitations' && <SectionEmpty title="Invitations" copy="Only FINDs you are eligible for appear here. We'll show you invitations as soon as they arrive." />}
          {tab === 'finds' && <SectionEmpty title="My FINDs" copy="Your accepted, scheduled, submitted and historical FINDs will show up here." />}
          {tab === 'ratings' && <RatingsView me={me} />}
          {tab === 'earnings' && <EarningsView me={me} />}
          {tab === 'achievements' && <AchievementsView me={me} />}
          {tab === 'referrals' && <ReferralsView me={me} />}
          {tab === 'affiliate' && <AffiliateView me={me} />}
          {tab === 'profile' && <ProfileView me={me} onSaved={setMe} />}
        </main>
      </div>
    </div>
  );
}

/* ─── Home ─────────────────────────────────────────────────────── */
function HomeView({ me, greetingName, ready, goTo }) {
  const cur = me.earnings.currency;
  const next = me.profile_completeness_detail?.next_action;
  return (
    <>
      <div className="sfp-hero" data-testid="sfp-home-hero">
        <div>
          <div className="eyebrow">Your satisFINDER home</div>
          <h1>{greeting()}, {greetingName}.</h1>
          <p>Your FINDs, invitations, rating, achievements and profile in one place.</p>
        </div>
        {ready ? (
          <div className="ready" data-testid="sfp-ready-badge">Ready for FINDs</div>
        ) : next ? (
          <button
            type="button"
            className="ready not as-link"
            onClick={() => goTo(next.tab)}
            data-testid="sfp-ready-badge"
          >{next.label} →</button>
        ) : (
          <div className="ready not" data-testid="sfp-ready-badge">Profile complete</div>
        )}
      </div>

      <div className="sfp-grid-2">
        <div className="sfp-card" data-testid="sfp-next-find">
          <div className="label">Next scheduled FIND</div>
          <h2>No FIND scheduled</h2>
          <p className="muted">When you accept an invitation, your next FIND appears here.</p>
        </div>
        <div className="sfp-card" data-testid="sfp-profile-snap">
          <h3>Your profile</h3>
          <p className="muted">Keep it current for better matching.</p>
          <div style={{ marginTop: 8 }} className="sfp-list-row"><div className="ico">1</div><div><b>Identity & contact</b><small>{me.identity.email_verified ? 'Verified' : 'Verify email'}</small></div></div>
          <div className="sfp-list-row"><div className="ico">2</div><div><b>Where I can do assignments</b><small>{me.coverage.city ? 'Set' : 'Add your city'}</small></div></div>
          <div className="sfp-list-row"><div className="ico">3</div><div><b>Brands</b><small>{(me.brands || []).length ? `${me.brands.length} brands` : 'Add brands you use'}</small></div></div>
          <div className="sfp-list-row"><div className="ico">4</div><div><b>Terms & privacy</b><small>{me.legal.accepted ? 'Accepted' : 'Accept to continue'}</small></div></div>
        </div>
      </div>

      <div className="sfp-stats" data-testid="sfp-home-stats">
        <div className="sfp-card"><div className="stat-val">{me.ratings.overall ?? '—'}</div><div className="stat-lbl">Overall rating</div><div className="stat-hint">{me.ratings.empty_reason || 'Average of rated FINDs'}</div></div>
        <div className="sfp-card"><div className="stat-val">{me.ratings.count || 0}</div><div className="stat-lbl">Published FINDs</div><div className="stat-hint">Since {me.achievements.satisfinder_since_year || new Date().getFullYear()}</div></div>
        <div className="sfp-card"><div className="stat-val">{money(me.earnings.fees, cur)}</div><div className="stat-lbl">FIND fees earned</div><div className="stat-hint">Lifetime</div></div>
        <div className="sfp-card"><div className="stat-val">{money(me.earnings.experience_value, cur)}</div><div className="stat-lbl">Experience value</div><div className="stat-hint">Reimbursements</div></div>
      </div>
    </>
  );
}

/* ─── Ratings ──────────────────────────────────────────────────── */
function RatingsView({ me }) {
  const r = me.ratings;
  return (
    <>
      <div className="sfp-hero"><div><div className="eyebrow">Performance</div><h1>Ratings</h1><p>Overall Rating is the average of rated FINDs. Industry Ratings use only FINDs in that industry.</p></div></div>
      <div className="sfp-grid-2b">
        <div className="sfp-card" data-testid="sfp-overall-rating">
          <div className="stat-val" style={{ fontSize: 44 }}>{r.overall ?? '—'}</div>
          <div className="muted">{r.overall ? `Overall rating · ${r.count} FINDs` : (r.empty_reason || 'No ratings yet')}</div>
        </div>
        <div className="sfp-card">
          <h3>By industry</h3>
          {(r.industry || []).length === 0
            ? <div className="sfp-empty">Industry ratings will appear once you have rated FINDs.</div>
            : r.industry.map((i) => <div key={i.industry} className="sfp-list-row"><div className="ico">•</div><div><b>{i.industry}</b><small>{i.count} FINDs</small></div><span className="sfp-badge blue">{i.avg} / 5</span></div>)}
        </div>
      </div>
      <div className="sfp-card">
        <h3>Recent FIND ratings</h3>
        {(r.recent || []).length === 0
          ? <div className="sfp-empty">No FIND ratings yet.</div>
          : r.recent.map(x => <div key={x.find_id} className="sfp-list-row"><div className="ico">★</div><div><b>{x.find_id}</b><small>{x.industry}</small></div><span className="sfp-badge">{x.rating} / 5</span></div>)}
      </div>
    </>
  );
}

/* ─── Earnings ─────────────────────────────────────────────────── */
function EarningsView({ me }) {
  const { currency, fees, experience_value, misc, total, transactions, empty_reason } = me.earnings;
  return (
    <>
      <div className="sfp-hero"><div><div className="eyebrow">Money & experiences</div><h1>Earnings</h1><p>Displayed in your profile currency ({currency}). Historical transactions keep their original currency.</p></div></div>
      <div className="sfp-stats">
        <div className="sfp-card"><div className="stat-val">{money(fees, currency)}</div><div className="stat-lbl">FIND fees</div></div>
        <div className="sfp-card"><div className="stat-val">{money(experience_value, currency)}</div><div className="stat-lbl">Experience value</div></div>
        <div className="sfp-card"><div className="stat-val">{money(misc, currency)}</div><div className="stat-lbl">Other approved expenses</div></div>
        <div className="sfp-card"><div className="stat-val">{money(total, currency)}</div><div className="stat-lbl">Total processed value</div></div>
      </div>
      <div className="sfp-card">
        <h3>Transactions</h3>
        {(!transactions || transactions.length === 0)
          ? <div className="sfp-empty">{empty_reason}</div>
          : transactions.map(t => <div key={t.id} className="sfp-list-row"><div className="ico">₹</div><div><b>{t.label}</b><small>{t.date}</small></div><span>{money(t.amount, t.currency || currency)}</span></div>)}
      </div>
    </>
  );
}

/* ─── Achievements ─────────────────────────────────────────────── */
function AchievementsView({ me }) {
  const a = me.achievements;
  return (
    <>
      <div className="sfp-hero"><div><div className="eyebrow">Recognition</div><h1>Achievements</h1><p>Badges, certifications and quarterly recognition you have earned.</p></div></div>
      <div className="sfp-grid-2b">
        <div className="sfp-card"><div className="sfp-list-row"><div className="ico">★</div><div><b>Certified satisFINDER</b><small>Complete your onboarding + optional TrialFIND</small></div><span className={`sfp-badge ${a.certified_satisfinder ? 'gold' : ''}`}>{a.certified_satisfinder ? 'Certified' : 'Not yet'}</span></div></div>
        <div className="sfp-card"><div className="sfp-list-row"><div className="ico">A</div><div><b>satisFINDER Ambassador</b><small>Complete profile · TrialFIND · 20+ FINDs · 4.5+ · referrals</small></div><span className="sfp-badge">{a.ambassador ? 'Earned' : 'Pending'}</span></div></div>
        <div className="sfp-card"><div className="sfp-list-row"><div className="ico">✓</div><div><b>Certificate of Completion</b><small>First qualifying 5-star FIND</small></div><span className="sfp-badge">—</span></div></div>
        <div className="sfp-card"><div className="sfp-list-row"><div className="ico">Q</div><div><b>Quarterly Certificate of Recognition</b><small>3+ qualifying 5-star FINDs in a quarter</small></div><span className="sfp-badge">—</span></div></div>
      </div>
    </>
  );
}

/* ─── Referrals: inviting people to become satisFINDERs ────────── */
function ReferralsView({ me }) {
  const counts = me.referrals.counts;
  return (
    <>
      <div className="sfp-hero"><div><div className="eyebrow">Community</div><h1>Referrals</h1><p>Invite people to become satisFINDERs and track who has joined.</p></div></div>
      <div className="sfp-card" data-testid="sfp-referrals">
        <h3>My referrals</h3>
        <div className="sfp-notice">
          You have {counts.total} referral{counts.total === 1 ? '' : 's'} · {counts.verified} verified · {counts.active} active.
        </div>
        <div style={{ marginTop: 14 }}>
          <div className="label">Your referral code</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3E6F', margin: '4px 0 12px' }} data-testid="sfp-referral-code">{me.identity.sf_public_id}</div>
        </div>
        <div style={{ marginTop: 4 }}>
          {(me.referrals.referrals || []).length === 0
            ? <div className="sfp-empty">Share your referral code to invite friends to become satisFINDERs.</div>
            : me.referrals.referrals.map(r => (
              <div key={r.sf_public_id || r.display_name} className="sfp-list-row">
                <div className="ico">{initials(r.display_name)}</div>
                <div><b>{r.display_name}</b><small>{r.signed_up_at?.slice(0,10)}</small></div>
                <span className={`sfp-badge ${r.status === 'active' ? 'green' : (r.status === 'verified' ? 'blue' : '')}`}>{r.status}</span>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

/* ─── Affiliate: referring clients for satisFIND services ──────── */
function AffiliateView({ me }) {
  return (
    <>
      <div className="sfp-hero"><div><div className="eyebrow">Community</div><h1>Affiliate</h1><p>Refer businesses and clients to satisFIND services using your affiliate code.</p></div></div>
      <div className="sfp-card" data-testid="sfp-affiliate">
        <h3>Your affiliate code</h3>
        <div className="sfp-notice">Share this code with businesses you introduce to satisFIND services.</div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3E6F', margin: '4px 0 12px' }} data-testid="sfp-affiliate-code">{me.affiliate.code}</div>
          <button className="sfp-secondary" onClick={async () => {
            try { await navigator.clipboard.writeText(me.affiliate.code); }
            catch {
              try {
                const ta = document.createElement('textarea');
                ta.value = me.affiliate.code;
                ta.style.position = 'fixed'; ta.style.opacity = '0';
                document.body.appendChild(ta); ta.select();
                document.execCommand('copy'); document.body.removeChild(ta);
              } catch { /* clipboard unavailable in this context */ }
            }
            toast.success('Affiliate code copied');
          }} data-testid="sfp-affiliate-copy">Copy affiliate code</button>
        </div>
      </div>
    </>
  );
}

/* ─── Profile ──────────────────────────────────────────────────── */
export function ProfileView({ me, onSaved, readOnly = false }) {
  const [tab, setTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    personal: { first_name: me.identity.first_name || '', middle_name: me.identity.middle_name || '', last_name: me.identity.last_name || '',
                dob: me.personal.dob || '', gender: me.personal.gender || '', marital_status: me.personal.marital_status || '',
                children: me.personal.children || '', accessibility_participation: me.personal.accessibility_participation || 'No' },
    coverage: { country: me.coverage.country || '', postal_code: me.coverage.postal_code || '', state: me.coverage.state || '', city: me.coverage.city || '', additional_areas: me.coverage.additional_areas || [] },
    languages: me.languages || [],
    brands: me.brands || [],
    social: {
      linkedin:  me.social_profiles?.linkedin  || '',
      instagram: me.social_profiles?.instagram || '',
      facebook:  me.social_profiles?.facebook  || '',
      tiktok:    me.social_profiles?.tiktok    || '',
      youtube:   me.social_profiles?.youtube   || '',
      other:     me.social_profiles?.other     || '',
    },
    comm: { onboarding_tips: me.comm_prefs.onboarding_tips, community_news: me.comm_prefs.community_news, marketing: me.comm_prefs.marketing, promotional: me.comm_prefs.promotional },
  });
  const [brandCategory, setBrandCategory] = useState('Restaurants');
  const [brandInput, setBrandInput] = useState('');
  const [brandSuggest, setBrandSuggest] = useState([]);
  const [addAreaInput, setAddAreaInput] = useState('');
  const [postalStatus, setPostalStatus] = useState('idle'); // 'idle' | 'loading' | 'matched' | 'no-match'
  const [photoBusy, setPhotoBusy] = useState(false);
  const [langSearch, setLangSearch] = useState('');
  // Read-only viewer for the Code of Conduct & Confidentiality Agreement.
  const [showCoC, setShowCoC] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (brandInput.length < 2) { setBrandSuggest([]); return; }
    const t = setTimeout(async () => {
      const r = await fetch(`${API}/satisfinder/brands/search?q=${encodeURIComponent(brandInput)}&category=${encodeURIComponent(brandCategory)}`, { credentials: 'include' });
      if (!cancel && r.ok) setBrandSuggest((await r.json()).suggestions);
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [brandInput, brandCategory]);

  // Postal auto-fill: when country + postal_code are set, resolve
  // city/state via the same `/satisfinder/postal-area-suggest`
  // endpoint the signup form already uses (which internally delegates
  // to /api/geo/postal-lookup: Nominatim primary + Zippopotam
  // fallback + Nominatim reverse enrichment). `area` in the response
  // is the state/province — filling the state gap the user hit for
  // India 500084. Silent no-op when the lookup returns nothing.
  useEffect(() => {
    if (readOnly) return;
    const country = (form.coverage.country || '').trim();
    const postal = (form.coverage.postal_code || '').trim();
    if (!country || !postal) { setPostalStatus('idle'); return; }
    let cancel = false;
    setPostalStatus('loading');
    const t = setTimeout(async () => {
      try {
        const q = new URLSearchParams({ country, postal_code: postal });
        const r = await fetch(`${API}/satisfinder/postal-area-suggest?${q.toString()}`, { credentials: 'include' });
        if (!r.ok) { if (!cancel) setPostalStatus('no-match'); return; }
        const d = await r.json();
        const rows = d.areas || [];
        if (cancel) return;
        if (rows.length === 0) { setPostalStatus('no-match'); return; }
        // Distinct cities — one match means clean auto-fill; multiple
        // means we still pick the first but leave both fields editable.
        const top = rows[0];
        const stateFromApi = (top.area && top.area !== top.city) ? top.area : '';
        setForm((prev) => ({
          ...prev,
          coverage: {
            ...prev.coverage,
            city:  prev.coverage.city  || top.city   || '',
            state: prev.coverage.state || stateFromApi || '',
          },
        }));
        setPostalStatus('matched');
      } catch { if (!cancel) setPostalStatus('no-match'); }
    }, 400);
    return () => { cancel = true; clearTimeout(t); };
  }, [form.coverage.country, form.coverage.postal_code]);

  const save = async (section, body) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/satisfinder/me/profile`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      toast.success(`${section} saved`);
      const fresh = await fetch(`${API}/satisfinder/me/full`, { credentials: 'include' }).then(x => x.json());
      onSaved(fresh);
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const refresh = async () => {
    const fresh = await fetch(`${API}/satisfinder/me/full`, { credentials: 'include' }).then(x => x.json());
    onSaved(fresh);
  };

  const uploadPhoto = async (fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/satisfinder/me/photo`, { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || ''); }
      toast.success('Profile photo updated');
      await refresh();
    } catch (e) { toast.error(e.message || 'Photo upload failed'); }
    finally { setPhotoBusy(false); fileEl.value = ''; }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      const r = await fetch(`${API}/satisfinder/me/photo`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Profile photo removed');
      await refresh();
    } catch { toast.error('Could not remove photo'); }
    finally { setPhotoBusy(false); }
  };

  return (
    <>
      {!readOnly && <div className="sfp-hero"><div><div className="eyebrow">Your information</div><h1>Profile</h1><p>This helps us match you with the right FINDs.</p></div></div>}
      <div className="sfp-card" data-testid={readOnly ? 'sfp-profile-readonly' : undefined}>
        <div className="sfp-tabs">
          {[['personal','Personal'],['coverage','Where I can do assignments'],['brands','Brands'],['languages','Languages'],['social','Social'],['comm','Communication'],['terms','Terms & privacy']].map(([k,l]) =>
            <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)} data-testid={`sfp-profile-tab-${k}`}>{l}</button>)}
        </div>

        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {tab === 'personal' && (
          <>
            <div className="sfp-photo" data-testid="sfp-photo-block">
              <div className="sfp-photo-av" data-testid="sfp-photo-preview">
                {me.identity.profile_photo_url
                  ? <img src={me.identity.profile_photo_url} alt="Profile" />
                  : initials(me.identity.full_name)}
              </div>
              {!readOnly && <div className="sfp-photo-actions">
                <div className="sfp-photo-btns">
                  <label className={`sfp-secondary${photoBusy ? ' is-busy' : ''}`} data-testid="sfp-photo-upload-label">
                    {photoBusy ? 'Uploading…' : (me.identity.profile_photo_url ? 'Replace photo' : 'Upload photo')}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      hidden
                      disabled={photoBusy}
                      onChange={(e) => uploadPhoto(e.target)}
                      data-testid="sfp-photo-input"
                    />
                  </label>
                  {me.identity.profile_photo_url && (
                    <button type="button" className="sfp-ghost" disabled={photoBusy} onClick={removePhoto} data-testid="sfp-photo-remove">Remove</button>
                  )}
                </div>
                <div className="hint">JPG, PNG or WEBP, up to 5MB. Shown beside your name.</div>
              </div>}
            </div>
            <div className="sfp-form">
              <div className="sfp-field"><label>First name</label><input value={form.personal.first_name} onChange={e => setForm({ ...form, personal: { ...form.personal, first_name: e.target.value } })} data-testid="sfp-first-name" /></div>
              <div className="sfp-field"><label>Last name</label><input value={form.personal.last_name} onChange={e => setForm({ ...form, personal: { ...form.personal, last_name: e.target.value } })} data-testid="sfp-last-name" /></div>
              <div className="sfp-field"><label>Date of birth</label><input type="date" value={form.personal.dob} onChange={e => setForm({ ...form, personal: { ...form.personal, dob: e.target.value } })} /></div>
              <div className="sfp-field"><label>Gender</label><select value={form.personal.gender} onChange={e => setForm({ ...form, personal: { ...form.personal, gender: e.target.value } })}><option value="">—</option><option>Female</option><option>Male</option><option>Prefer not to say</option></select></div>
              <div className="sfp-field"><label>Marital status</label><select value={form.personal.marital_status} onChange={e => setForm({ ...form, personal: { ...form.personal, marital_status: e.target.value } })}><option value="">—</option><option>Single</option><option>Partnered</option><option>Married</option><option>Prefer not to say</option></select></div>
              <div className="sfp-field"><label>Children</label><select value={form.personal.children} onChange={e => setForm({ ...form, personal: { ...form.personal, children: e.target.value } })}><option value="">—</option><option>0</option><option>1</option><option>2</option><option>3+</option></select></div>
              <div className="sfp-field full">
                <label>Accessibility / PWD participation</label>
                <select value={form.personal.accessibility_participation} onChange={e => setForm({ ...form, personal: { ...form.personal, accessibility_participation: e.target.value } })}>
                  <option>No</option><option>Yes, I am willing to be considered for relevant accessibility FINDs</option><option>Prefer not to say</option>
                </select>
                <div className="hint">Optional. Do not provide diagnosis or medical details.</div>
              </div>
            </div>
            {!readOnly && <div style={{ marginTop: 12 }}><button className="sfp-primary" disabled={saving} onClick={() => save('Personal', { personal: form.personal })} data-testid="sfp-save-personal">Save personal</button></div>}
          </>
        )}

        {tab === 'coverage' && (
          <>
            <div className="sfp-notice">Your primary/home location. This is where most of your assignments will be scheduled.</div>
            <div className="sfp-form">
              <div className="sfp-field"><label>Country</label><input value={form.coverage.country} onChange={e => setForm({ ...form, coverage: { ...form.coverage, country: e.target.value } })} placeholder="e.g. Philippines" data-testid="sfp-coverage-country" /></div>
              <div className="sfp-field">
                <label>Postal / ZIP / PIN</label>
                <input value={form.coverage.postal_code} onChange={e => setForm({ ...form, coverage: { ...form.coverage, postal_code: e.target.value, city: '', state: '' } })} data-testid="sfp-coverage-postal" />
                <div className="hint">
                  {postalStatus === 'loading' && 'Looking up city and state…'}
                  {postalStatus === 'matched' && 'City and state auto-filled from postal code — you can override.'}
                  {postalStatus === 'no-match' && 'No match from postal lookup — please enter city and state manually.'}
                  {postalStatus === 'idle' && 'City and state auto-fill once postal code is entered.'}
                </div>
              </div>
              <div className="sfp-field"><label>State / Province</label><input value={form.coverage.state} onChange={e => setForm({ ...form, coverage: { ...form.coverage, state: e.target.value } })} data-testid="sfp-coverage-state" /></div>
              <div className="sfp-field"><label>City</label><input value={form.coverage.city} onChange={e => setForm({ ...form, coverage: { ...form.coverage, city: e.target.value } })} data-testid="sfp-coverage-city" /></div>
            </div>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--sf-line)' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Additional cities/areas where I can do assignments</h3>
              <div className="hint" style={{ marginBottom: 10 }}>Optional. Add any other cities or neighbourhoods where you are willing to accept assignments. These are separate from your primary/home location above.</div>
              <div className="sfp-field full">
                {!readOnly && <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={addAreaInput}
                    onChange={(e) => setAddAreaInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = addAreaInput.trim();
                        if (!v) return;
                        if (form.coverage.additional_areas.some(x => x.toLowerCase() === v.toLowerCase())) { setAddAreaInput(''); return; }
                        setForm({ ...form, coverage: { ...form.coverage, additional_areas: [...form.coverage.additional_areas, v] } });
                        setAddAreaInput('');
                      }
                    }}
                    placeholder="e.g. Banjara Hills"
                    data-testid="sfp-add-area-input"
                  />
                  <button
                    type="button"
                    className="sfp-secondary"
                    onClick={() => {
                      const v = addAreaInput.trim();
                      if (!v) return;
                      if (form.coverage.additional_areas.some(x => x.toLowerCase() === v.toLowerCase())) { setAddAreaInput(''); return; }
                      setForm({ ...form, coverage: { ...form.coverage, additional_areas: [...form.coverage.additional_areas, v] } });
                      setAddAreaInput('');
                    }}
                    data-testid="sfp-add-area-btn"
                  >Add</button>
                </div>}
                <div className="tagwrap" style={{ marginTop: 8 }} data-testid="sfp-additional-areas">
                  {form.coverage.additional_areas.length === 0 && <span className="hint">No additional cities/areas yet.</span>}
                  {form.coverage.additional_areas.map((area, i) => (
                    <span key={`${area}-${i}`} className="sfp-tag" data-testid={`sfp-area-chip-${i}`}>
                      {area}
                      {!readOnly && <button
                        type="button"
                        onClick={() => setForm({ ...form, coverage: { ...form.coverage, additional_areas: form.coverage.additional_areas.filter((_, j) => j !== i) } })}
                        data-testid={`sfp-area-remove-${i}`}
                      >×</button>}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {!readOnly && <div style={{ marginTop: 12 }}><button className="sfp-primary" disabled={saving} onClick={() => save('Coverage', { coverage: form.coverage })} data-testid="sfp-save-coverage">Save coverage</button></div>}
          </>
        )}

        {tab === 'brands' && (
          <>
            {!readOnly && <div className="sfp-notice">Unaided recall first. Pick a category, then start typing a brand you have used in the last 12 months.</div>}
            <div className="sfp-form" style={{ marginTop: 12 }}>
              {!readOnly && <div className="sfp-field"><label>Category</label><select value={brandCategory} onChange={e => setBrandCategory(e.target.value)}><option>Restaurants</option><option>Hotels</option><option>Banks</option><option>Telecom</option><option>Retail</option><option>Travel</option></select></div>}
              {!readOnly && <div className="sfp-field">
                <label>Which brands have you personally used?</label>
                <input value={brandInput} onChange={e => setBrandInput(e.target.value)} placeholder="Type at least 2 characters…" data-testid="sfp-brand-input" />
                {brandSuggest.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {brandSuggest.map(s => <button key={s.canonical_id} className="sfp-tag" onClick={() => setForm({ ...form, brands: [...form.brands, { canonical_id: s.canonical_id, name: s.name, category: s.category, is_suggested: false }] })}>{s.name}</button>)}
                  </div>
                )}
                {brandInput.length >= 2 && brandSuggest.length === 0 && (
                  <button className="sfp-secondary" style={{ marginTop: 6 }} onClick={() => { setForm({ ...form, brands: [...form.brands, { name: brandInput, category: brandCategory, is_suggested: true }] }); setBrandInput(''); }}>
                    Suggest "{brandInput}"
                  </button>
                )}
              </div>}
              <div className="sfp-field full">
                <label>Brands on your profile</label>
                <div className="tagwrap" data-testid="sfp-brands-list">
                  {form.brands.length === 0 && <span className="hint">No brands yet.</span>}
                  {form.brands.map((b, i) => <span key={i} className="sfp-tag">{b.name}{b.is_suggested ? ' *' : ''}{!readOnly && <button onClick={() => setForm({ ...form, brands: form.brands.filter((_, j) => j !== i) })}>×</button>}</span>)}
                </div>
              </div>
            </div>
            {!readOnly && <div style={{ marginTop: 12 }}><button className="sfp-primary" disabled={saving} onClick={() => save('Brands', { brands: { brands: form.brands } })}>Save brands</button></div>}
          </>
        )}

        {tab === 'languages' && (
          <>
            {!readOnly && <div className="sfp-notice">Select all the languages you can speak. Search the predefined list, or add your own if it is not shown. Your existing languages are kept.</div>}
            <div className="sfp-field full" style={{ marginTop: 12 }}>
              <label>Languages you speak</label>
              <div className="tagwrap" style={{ minHeight: 56 }} data-testid="sfp-lang-selected">
                {form.languages.length === 0 && <span className="hint">No languages selected yet.</span>}
                {form.languages.map((l, i) => (
                  <span key={`${l}-${i}`} className="sfp-tag" data-testid={`sfp-lang-chip-${i}`}>
                    {l}
                    {!readOnly && <button type="button" onClick={() => setForm({ ...form, languages: form.languages.filter((_, j) => j !== i) })} data-testid={`sfp-lang-remove-${i}`}>×</button>}
                  </span>
                ))}
              </div>
            </div>
            {!readOnly && <div className="sfp-field full">
              <label>Add languages</label>
              <input
                value={langSearch}
                onChange={(e) => setLangSearch(e.target.value)}
                placeholder="Search languages…"
                data-testid="sfp-lang-search"
              />
              <div className="sfp-lang-results" data-testid="sfp-lang-results">
                {(() => {
                  const q = langSearch.trim().toLowerCase();
                  const selected = new Set(form.languages.map(x => x.toLowerCase()));
                  const matches = LANGUAGE_OPTIONS
                    .filter(opt => !selected.has(opt.toLowerCase()) && (!q || opt.toLowerCase().includes(q)))
                    .slice(0, 14);
                  const addLang = (val) => {
                    const v = (val || '').trim();
                    if (!v || selected.has(v.toLowerCase())) { setLangSearch(''); return; }
                    setForm({ ...form, languages: [...form.languages, v] });
                    setLangSearch('');
                  };
                  return (
                    <>
                      {matches.map(opt => (
                        <button key={opt} type="button" className="sfp-lang-opt" onClick={() => addLang(opt)} data-testid="sfp-lang-opt">{opt}</button>
                      ))}
                      {matches.length === 0 && q && !selected.has(q) && (
                        <button type="button" className="sfp-lang-opt add" onClick={() => addLang(langSearch)} data-testid="sfp-lang-add-custom">Add &quot;{langSearch.trim()}&quot;</button>
                      )}
                      {matches.length === 0 && !q && <span className="hint">Start typing to search the language list.</span>}
                    </>
                  );
                })()}
              </div>
            </div>}
            {!readOnly && <div style={{ marginTop: 12 }}><button className="sfp-primary" disabled={saving} onClick={() => save('Languages', { languages: { languages: form.languages } })} data-testid="sfp-save-languages">Save languages</button></div>}
          </>
        )}

        {tab === 'social' && (
          <>
            <div className="sfp-notice">Social links are optional. Add either a username/handle or the full profile URL. These do not affect your Ready for FINDs status.</div>
            <div className="sfp-form" style={{ marginTop: 12 }}>
              {[
                ['linkedin',  'LinkedIn',  'linkedin.com/in/your-handle'],
                ['instagram', 'Instagram', '@your.handle'],
                ['facebook',  'Facebook',  'facebook.com/your.profile'],
                ['tiktok',    'TikTok',    '@your.handle'],
                ['youtube',   'YouTube',   '@yourchannel or channel URL'],
                ['other',     'Other',     'Any other link you\u2019d like to share'],
              ].map(([key, label, placeholder]) => (
                <div key={key} className={`sfp-field${key === 'other' ? ' full' : ''}`}>
                  <label>{label}</label>
                  <input
                    value={form.social[key]}
                    onChange={(e) => setForm({ ...form, social: { ...form.social, [key]: e.target.value } })}
                    placeholder={placeholder}
                    data-testid={`sfp-social-${key}`}
                  />
                </div>
              ))}
            </div>
            {!readOnly && <div style={{ marginTop: 12 }}>
              <button
                className="sfp-primary"
                disabled={saving}
                onClick={() => {
                  // Only send non-empty entries; PUT overwrites the map,
                  // so this cleanly removes any that were cleared.
                  const social_profiles = Object.fromEntries(
                    Object.entries(form.social)
                      .map(([k, v]) => [k, (v || '').trim()])
                      .filter(([, v]) => v)
                  );
                  save('Social links', { social: { social_profiles } });
                }}
                data-testid="sfp-save-social"
              >Save social</button>
            </div>}
          </>
        )}

        {tab === 'comm' && (
          <>
            <div className="sfp-check"><input type="checkbox" checked disabled /><div><b>Essential satisFIND service emails</b><span>Account security, FIND invitations, scheduling, QA requests, payments, important service notices. Always on while your account is active.</span></div></div>
            <div className="sfp-check"><input type="checkbox" checked={form.comm.onboarding_tips} onChange={e => setForm({ ...form, comm: { ...form.comm, onboarding_tips: e.target.checked } })} data-testid="sfp-comm-tips" /><div><b>Onboarding tips</b><span>Helpful nudges while you get set up.</span></div></div>
            <div className="sfp-check"><input type="checkbox" checked={form.comm.community_news} onChange={e => setForm({ ...form, comm: { ...form.comm, community_news: e.target.checked } })} /><div><b>Community news</b><span>What is happening in the satisFINDER community.</span></div></div>
            <div className="sfp-check"><input type="checkbox" checked={form.comm.marketing} onChange={e => setForm({ ...form, comm: { ...form.comm, marketing: e.target.checked } })} /><div><b>Marketing</b><span>Offers, launches and product news.</span></div></div>
            <div className="sfp-check"><input type="checkbox" checked={form.comm.promotional} onChange={e => setForm({ ...form, comm: { ...form.comm, promotional: e.target.checked } })} /><div><b>Promotional</b><span>Third-party promotions relevant to satisFINDERs.</span></div></div>
            {!readOnly && <div style={{ marginTop: 12 }}><button className="sfp-primary" disabled={saving} onClick={() => save('Communication', { comm_prefs: form.comm })} data-testid="sfp-save-comm">Save preferences</button></div>}
          </>
        )}
        </fieldset>

        {tab === 'terms' && (
          <div data-testid="sfp-terms-section">
            <div className="sfp-check">
              <input type="checkbox" checked={!!me.legal.latest?.terms_version} readOnly />
              <div>
                <b>Terms of Service accepted</b>
                <span>{me.legal.latest?.terms_version || 'Not accepted yet'}</span>
                <a className="sfp-policy-link" href="https://satisfind.com/terms" target="_blank" rel="noopener noreferrer" data-testid="sfp-policy-link-terms">Read Terms of Service →</a>
              </div>
            </div>
            <div className="sfp-check">
              <input type="checkbox" checked={!!me.legal.latest?.privacy_policy_version} readOnly />
              <div>
                <b>Privacy Policy acknowledged</b>
                <span>{me.legal.latest?.privacy_policy_version || 'Not accepted yet'}</span>
                <a className="sfp-policy-link" href="https://satisfind.com/privacy" target="_blank" rel="noopener noreferrer" data-testid="sfp-policy-link-privacy">Read Privacy Policy →</a>
              </div>
            </div>
            <div className="sfp-check">
              <input type="checkbox" checked={!!me.legal.latest?.confidentiality_agreement_version} readOnly />
              <div>
                <b>Confidentiality & mystery shopping terms accepted</b>
                <span>{me.legal.latest?.confidentiality_agreement_version || 'Not accepted yet'}</span>
                <button type="button" className="sfp-policy-link" onClick={() => setShowCoC(true)} data-testid="sfp-policy-link-confidentiality">View Code of Conduct &amp; Confidentiality Agreement →</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showCoC && (
        <AgreementViewer
          accepted={me.legal.latest?.confidentiality_agreement_version}
          onClose={() => setShowCoC(false)}
        />
      )}
    </>
  );
}

/* ─── Code of Conduct & Confidentiality Agreement (read-only viewer) ─── */
function AgreementViewer({ accepted, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="sfp-modal-overlay" onClick={onClose} data-testid="sfp-coc-modal">
      <div className="sfp-modal" role="dialog" aria-modal="true" aria-label={SATISFINDER_AGREEMENT.title} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="sfp-modal-close" onClick={onClose} aria-label="Close" data-testid="sfp-coc-modal-close">×</button>
        <div className="sfp-modal-eyebrow">{SATISFINDER_AGREEMENT.eyebrow}</div>
        <h2 className="sfp-modal-title">{SATISFINDER_AGREEMENT.title}</h2>
        {accepted && (
          <div className="sfp-modal-accepted" data-testid="sfp-coc-accepted">Accepted · version {accepted}</div>
        )}
        <div className="sfp-modal-body">
          <p>{SATISFINDER_AGREEMENT.summary}</p>
          <p className="sfp-modal-subhead">{SATISFINDER_AGREEMENT.commitmentsHeading}</p>
          <ul>
            {SATISFINDER_AGREEMENT.commitments.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
        <button type="button" className="sfp-primary" onClick={onClose} data-testid="sfp-coc-modal-done">Close</button>
      </div>
    </div>
  );
}

/* ─── Section empty state ──────────────────────────────────────── */
function SectionEmpty({ title, copy }) {
  return (
    <>
      <div className="sfp-hero"><div><div className="eyebrow">Opportunities</div><h1>{title}</h1><p>{copy}</p></div></div>
      <div className="sfp-card"><div className="sfp-empty">Nothing here yet.</div></div>
    </>
  );
}
